import { Link } from "react-router-dom";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { finnhouseLogoUrl, onTeamLogoError, teamLogoUrl } from "../../utils/teamLogo";

type TeamLogoProps = {
  teamId: string;
  alt?: string;
  className?: string;
  loading?: "lazy" | "eager";
  /** Intrinsic size hint for CLS — display size still comes from CSS className */
  width?: number;
  height?: number;
  /**
   * When true (default), wraps the logo in a link to the team page.
   * Disable when nested inside another link/button that handles the click.
   */
  linkToTeam?: boolean;
};

const isLinkableTeamId = (teamId: string): boolean => {
  const id = teamId.trim();
  return id.length > 0 && !/^tbd$/i.test(id);
};

export const TeamLogo = ({
  teamId,
  alt,
  className,
  loading = "lazy",
  width = 64,
  height = 64,
  linkToTeam = true,
}: TeamLogoProps) => {
  const { localized } = useLocalePath();
  const { strings } = useLocalization();
  const resolvedAlt = alt ?? `${teamId} logo`;
  const image = (
    <img
      src={teamLogoUrl(teamId)}
      alt={resolvedAlt}
      className={className}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      onError={onTeamLogoError}
    />
  );

  if (!linkToTeam || !isLinkableTeamId(teamId)) {
    return image;
  }

  const teamLabel = alt?.trim() || teamId;

  return (
    <Link
      to={localized(`/fpl/teams/${encodeURIComponent(teamId.trim())}`)}
      className="inline-flex shrink-0 items-center justify-center leading-none text-inherit no-underline hover:opacity-[0.88] focus-visible:rounded-[0.35rem] focus-visible:opacity-[0.88] focus-visible:shadow-[0_0_0_2px_rgba(123,147,255,0.55)] focus-visible:outline-none"
      aria-label={strings.TEAMS.VIEW_TEAM.replace("{team}", teamLabel)}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {image}
    </Link>
  );
};

export const TeamLogoFromSrc = ({
  src,
  alt,
  className,
  loading = "lazy",
  width = 64,
  height = 64,
}: {
  src: string;
  alt?: string;
  className?: string;
  loading?: "lazy" | "eager";
  width?: number;
  height?: number;
}) => (
  <img
    src={src}
    alt={alt ?? "Team logo"}
    className={className}
    width={width}
    height={height}
    loading={loading}
    decoding="async"
    onError={onTeamLogoError}
  />
);

export { finnhouseLogoUrl, teamLogoUrl };
